import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Ticket,
  TicketStats,
  TicketTag,
  AdminUser,
  STATUS_LABELS,
  PRIORITY_LABELS,
  DEPARTMENTS,
  TicketStatus
} from './core/_models'
import {
  getTicketStats,
  getTickets,
  getAdminUsers,
  getTags,
  updateTicket,
  assignTicket,
  bulkAssignTickets,
  bulkUpdateStatus
} from './core/_requests'
import KanbanBoard from './KanbanBoard'
import TagManager from './TagManager'
import CreateTicketModal from './CreateTicketModal'
import moment from 'moment'
import 'moment/locale/he'
moment.locale('he')

type ViewMode = 'list' | 'kanban'

const STATUS_TAB_ORDER: (TicketStatus | 'all')[] = ['all', 'open', 'in_progress', 'in_development', 'waiting_on_customer', 'follow_up', 'resolved', 'closed']

const STATUS_DOT_COLORS: Record<string, string> = {
  open: '#f64e60',
  in_progress: '#ffa800',
  in_development: '#3699ff',
  waiting_on_customer: '#8950fc',
  follow_up: '#e67e22',
  resolved: '#1bc5bd',
  closed: '#7e8299',
}

const TicketsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  // State
  const [stats, setStats] = useState<TicketStats | null>(null)
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [allTags, setAllTags] = useState<TicketTag[]>([])
  const [selectedTickets, setSelectedTickets] = useState<string[]>([])

  // View mode (persisted in localStorage)
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    (localStorage.getItem('tickets_view_mode') as ViewMode) || 'kanban'
  )

  // Filter state
  const statusFilter = searchParams.get('status') || 'all'
  const tagFilter = searchParams.get('tag') || ''
  const priorityFilter = searchParams.get('priority') || ''
  const assignedFilter = searchParams.get('assigned') || ''
  const followUpFilter = searchParams.get('follow_up_due') === 'true'
  const departmentFilter = searchParams.get('department') || ''
  const internalFilter = searchParams.get('is_internal') === 'true'
  const searchQuery = searchParams.get('search') || ''
  const [localSearch, setLocalSearch] = useState(searchQuery)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  // Modals
  const [showTagManager, setShowTagManager] = useState(false)
  const [showCreateTicket, setShowCreateTicket] = useState(false)

  // Status pill popover
  const [statusPopover, setStatusPopover] = useState<string | null>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Load data
  useEffect(() => {
    loadData()
  }, [statusFilter, tagFilter, priorityFilter, assignedFilter, followUpFilter, departmentFilter, internalFilter, searchQuery, currentPage])

  // Close popover on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setStatusPopover(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const params: any = {
        page: currentPage,
        limit: viewMode === 'kanban' ? 200 : 25,
        sort: '-updated_at',
      }

      // Status filter
      if (followUpFilter) {
        params.follow_up_due = true
      } else if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter
      } else if (viewMode === 'kanban') {
        // Kanban shows all non-closed by default
        params.status = 'all'
      } else {
        params.status = 'active'
      }

      // Other filters
      if (tagFilter) params.tag = tagFilter
      if (priorityFilter) params.priority = priorityFilter
      if (departmentFilter) params.department = departmentFilter
      if (internalFilter) params.is_internal = true
      if (assignedFilter === 'me') params.my_tickets = true
      else if (assignedFilter === 'unassigned') params.unassigned = true
      else if (assignedFilter) params.assigned_to = assignedFilter
      if (searchQuery) params.search = searchQuery

      const [statsRes, ticketsRes, adminsRes, tagsRes] = await Promise.all([
        getTicketStats(),
        getTickets(params),
        getAdminUsers(),
        getTags(),
      ])

      setStats(statsRes.data)
      setTickets(ticketsRes.data.tickets)
      setTotalPages(ticketsRes.data.pagination.pages)
      setAdmins(adminsRes.data.admins)
      setAllTags(tagsRes.data.tags)
    } catch (error) {
      console.error('Failed to load tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  const setFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    // Clear follow_up_due when switching status tabs
    if (key === 'status') {
      newParams.delete('follow_up_due')
    }
    setSearchParams(newParams)
    setCurrentPage(1)
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter('search', localSearch)
  }

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode)
    localStorage.setItem('tickets_view_mode', mode)
  }

  const handleStatusChange = async (ticketId: string, newStatus: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    try {
      await updateTicket(ticketId, { status: newStatus as any })
      setStatusPopover(null)
      loadData()
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const handleAssign = async (ticketId: string, adminId: string | null, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await assignTicket(ticketId, adminId)
      loadData()
    } catch (error) {
      console.error('Failed to assign ticket:', error)
    }
  }

  const handleBulkAssign = async (adminId: string | null) => {
    if (selectedTickets.length === 0) return
    try {
      await bulkAssignTickets(selectedTickets, adminId)
      setSelectedTickets([])
      loadData()
    } catch (error) {
      console.error('Failed to bulk assign:', error)
    }
  }

  const handleBulkStatusChange = async (status: string) => {
    if (selectedTickets.length === 0) return
    try {
      await bulkUpdateStatus(selectedTickets, status as any)
      setSelectedTickets([])
      loadData()
    } catch (error) {
      console.error('Failed to bulk update status:', error)
    }
  }

  const toggleSelectTicket = (ticketId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedTickets(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([])
    } else {
      setSelectedTickets(tickets.map(t => t._id))
    }
  }

  const getCustomerName = (ticket: Ticket) => {
    if (ticket.is_internal) {
      const creator = ticket.created_by && typeof ticket.created_by === 'object' ? ticket.created_by.username : ''
      return creator ? `פנימי · ${creator}` : 'פנימי'
    }
    if (typeof ticket.account === 'object' && ticket.account) {
      return `${ticket.account.first_name || ''} ${ticket.account.last_name || ''}`.trim() || ticket.account.email
    }
    return 'Unknown'
  }

  const getDepartmentLabel = (dept: string | null) => {
    if (!dept) return null
    return DEPARTMENTS.find(d => d.value === dept)?.label || dept
  }

  const getAssigneeName = (ticket: Ticket) => {
    if (ticket.assigned_to && typeof ticket.assigned_to === 'object') {
      return ticket.assigned_to.username
    }
    return null
  }

  const getTimeAgo = (date: string) => {
    const now = moment()
    const then = moment(date)
    const diff = now.diff(then, 'hours')

    if (diff < 1) return moment(date).fromNow()
    if (diff < 24) return `${diff} שעות`
    if (diff < 48) return 'אתמול'
    return moment(date).format('DD/MM')
  }

  const getTagObj = (tagName: string) => allTags.find(t => t.name === tagName)

  const getStatusCount = (status: string) => {
    if (!stats?.statusBreakdown) return 0
    if (status === 'all') {
      return Object.values(stats.statusBreakdown).reduce((sum, c) => sum + c, 0)
    }
    return stats.statusBreakdown[status] || 0
  }

  return (
    <div className="tickets-page">
      {/* Stats Cards */}
      <div className="stats-row">
        <div
          className={`stat-card ${statusFilter === 'open' ? 'active' : ''}`}
          onClick={() => setFilter('status', 'open')}
        >
          <div className="stat-icon open">
            <i className="bi bi-envelope-open"></i>
          </div>
          <div className="stat-value">{stats?.totalOpen || 0}</div>
          <div className="stat-label">פניות פתוחות</div>
        </div>

        <div
          className={`stat-card ${assignedFilter === 'unassigned' ? 'active' : ''}`}
          onClick={() => setFilter('assigned', assignedFilter === 'unassigned' ? '' : 'unassigned')}
        >
          <div className="stat-icon unassigned">
            <i className="bi bi-person-x"></i>
          </div>
          <div className="stat-value">{stats?.totalUnassigned || 0}</div>
          <div className="stat-label">לא משויכות</div>
        </div>

        <div
          className={`stat-card ${assignedFilter === 'me' ? 'active' : ''}`}
          onClick={() => setFilter('assigned', assignedFilter === 'me' ? '' : 'me')}
        >
          <div className="stat-icon my">
            <i className="bi bi-person-badge"></i>
          </div>
          <div className="stat-value">{stats?.myTickets || 0}</div>
          <div className="stat-label">הפניות שלי</div>
        </div>

        <div
          className={`stat-card ${followUpFilter ? 'active' : ''}`}
          onClick={() => {
            const newParams = new URLSearchParams(searchParams)
            if (followUpFilter) {
              newParams.delete('follow_up_due')
            } else {
              newParams.set('follow_up_due', 'true')
              newParams.delete('status')
            }
            setSearchParams(newParams)
            setCurrentPage(1)
          }}
        >
          <div className="stat-icon followup">
            <i className="bi bi-bell"></i>
          </div>
          <div className="stat-value">{stats?.followUpsDue || 0}</div>
          <div className="stat-label">מעקבים לטיפול</div>
        </div>
      </div>

      {/* Search Bar + New Ticket Button */}
      <div className="search-row">
        <form className="search-bar" onSubmit={handleSearch}>
          <i className="bi bi-search search-icon"></i>
          <input
            type="text"
            placeholder="חיפוש פניות..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
          />
          <span className="search-shortcut">Enter</span>
        </form>
        <button className="btn-discord new-ticket-btn" onClick={() => setShowCreateTicket(true)}>
          <i className="bi bi-plus-lg"></i>
          פנייה חדשה
        </button>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar">
        {/* Status Tabs */}
        <div className="filter-tabs">
          {STATUS_TAB_ORDER.map(status => (
            <button
              key={status}
              className={`filter-tab ${statusFilter === status && !followUpFilter ? 'active' : ''}`}
              onClick={() => setFilter('status', status === 'all' ? '' : status)}
            >
              {STATUS_LABELS[status]}
              <span className="tab-count">{getStatusCount(status)}</span>
            </button>
          ))}
        </div>

        {/* Tag Chips Filter */}
        <div className="filter-section">
          <span className="filter-label">תגיות:</span>
          <div className="filter-chips">
            {allTags.map(tag => (
              <button
                key={tag._id}
                className={`tag-filter-chip ${tagFilter === tag.name ? 'active' : ''}`}
                style={tagFilter === tag.name
                  ? { background: tag.color, color: 'white', borderColor: tag.color }
                  : { borderColor: `${tag.color}60`, color: tag.color }
                }
                onClick={() => setFilter('tag', tagFilter === tag.name ? '' : tag.name)}
              >
                {tag.name}
              </button>
            ))}
            <button className="tag-manage-btn" onClick={() => setShowTagManager(true)}>
              <i className="bi bi-gear"></i>
            </button>
          </div>
        </div>

        {/* Priority + Department + Assigned + Internal + View Toggle */}
        <div className="filter-row">
          <div className="filter-section">
            <span className="filter-label">עדיפות:</span>
            <div className="filter-chips">
              {(['', 'urgent', 'high'] as const).map(p => (
                <button
                  key={p}
                  className={`filter-chip ${priorityFilter === p ? 'active' : ''}`}
                  onClick={() => setFilter('priority', p)}
                >
                  {p === '' ? 'הכל' : PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>
          </div>

          <div className="filter-section">
            <span className="filter-label">מחלקה:</span>
            <select
              className="filter-select"
              value={departmentFilter}
              onChange={(e) => setFilter('department', e.target.value)}
            >
              <option value="">הכל</option>
              {DEPARTMENTS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-section">
            <span className="filter-label">שיוך:</span>
            <select
              className="filter-select"
              value={assignedFilter}
              onChange={(e) => setFilter('assigned', e.target.value)}
            >
              <option value="">הכל</option>
              <option value="me">שלי</option>
              <option value="unassigned">לא משויך</option>
              {admins.map(admin => (
                <option key={admin._id} value={admin._id}>{admin.username}</option>
              ))}
            </select>
          </div>

          <button
            className={`filter-chip ${internalFilter ? 'active' : ''}`}
            onClick={() => setFilter('is_internal', internalFilter ? '' : 'true')}
          >
            <i className="bi bi-building"></i>
            פנימיות
          </button>

          <div className="view-toggle">
            <button
              className={`toggle-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('kanban')}
              title="תצוגת קנבן"
            >
              <i className="bi bi-kanban"></i>
            </button>
            <button
              className={`toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => handleViewModeChange('list')}
              title="תצוגת רשימה"
            >
              <i className="bi bi-list-ul"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
        </div>
      ) : viewMode === 'kanban' ? (
        <KanbanBoard
          tickets={tickets}
          tags={allTags}
          onTicketClick={(id) => navigate(`/admin/tickets/${id}`)}
          onRefresh={loadData}
        />
      ) : (
        /* List View */
        <div className="ticket-list">
          <div className="list-header">
            <div className="header-title">
              <input
                type="checkbox"
                checked={selectedTickets.length === tickets.length && tickets.length > 0}
                onChange={toggleSelectAll}
                style={{ marginLeft: '12px', width: '18px', height: '18px' }}
              />
              פניות
              <span style={{ color: '#565674', marginRight: '8px' }}>
                ({tickets.length})
              </span>
            </div>
          </div>

          {/* Bulk Actions */}
          {selectedTickets.length > 0 && (
            <div className="bulk-actions">
              <span className="selected-count">{selectedTickets.length} נבחרו</span>
              <select
                onChange={(e) => handleBulkAssign(e.target.value || null)}
                defaultValue=""
              >
                <option value="">שייך ל...</option>
                <option value="">ללא שיוך</option>
                {admins.map(admin => (
                  <option key={admin._id} value={admin._id}>{admin.username}</option>
                ))}
              </select>
              <select
                onChange={(e) => e.target.value && handleBulkStatusChange(e.target.value)}
                defaultValue=""
              >
                <option value="">שנה סטטוס...</option>
                <option value="in_progress">בטיפול</option>
                <option value="waiting_on_customer">ממתין ללקוח</option>
                <option value="resolved">נפתר</option>
                <option value="closed">סגור</option>
              </select>
              <button className="clear-btn" onClick={() => setSelectedTickets([])}>
                בטל
              </button>
            </div>
          )}

          {tickets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <i className="bi bi-inbox"></i>
              </div>
              <div className="empty-title">אין פניות</div>
              <div className="empty-text">לא נמצאו פניות התואמות לחיפוש</div>
            </div>
          ) : (
            <>
              {tickets.map(ticket => (
                <div
                  key={ticket._id}
                  className={`ticket-card ${ticket.admin_unread ? 'unread' : ''}`}
                  onClick={() => navigate(`/admin/tickets/${ticket._id}`)}
                >
                  <div className="ticket-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTickets.includes(ticket._id)}
                      onChange={() => {}}
                      onClick={(e) => toggleSelectTicket(ticket._id, e)}
                    />
                  </div>

                  {/* Clickable status pill */}
                  <div className="status-pill-wrapper" onClick={(e) => e.stopPropagation()}>
                    <button
                      className={`status-pill ${ticket.status}`}
                      onClick={(e) => {
                        e.stopPropagation()
                        setStatusPopover(statusPopover === ticket._id ? null : ticket._id)
                      }}
                    >
                      <span className="pill-dot" style={{ background: STATUS_DOT_COLORS[ticket.status] }}></span>
                      {STATUS_LABELS[ticket.status]}
                    </button>
                    {statusPopover === ticket._id && (
                      <div className="status-popover" ref={popoverRef}>
                        {Object.entries(STATUS_LABELS)
                          .filter(([k]) => !['all', 'active'].includes(k) && k !== ticket.status)
                          .map(([value, label]) => (
                            <button
                              key={value}
                              className="popover-option"
                              onClick={(e) => handleStatusChange(ticket._id, value, e)}
                            >
                              <span className="pill-dot" style={{ background: STATUS_DOT_COLORS[value] }}></span>
                              {label}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="ticket-content">
                    <div className="ticket-header">
                      <span className="ticket-number">{ticket.ticket_number}</span>
                      <span className="ticket-subject">{ticket.subject}</span>
                      {ticket.admin_unread && <span className="unread-indicator"></span>}
                    </div>
                    <div className="ticket-meta">
                      <span className="customer-name">{getCustomerName(ticket)}</span>
                      {ticket.priority !== 'medium' && (
                        <span className={`priority-badge ${ticket.priority}`}>
                          {PRIORITY_LABELS[ticket.priority]}
                        </span>
                      )}
                      {getAssigneeName(ticket) && (
                        <span className="assignee">
                          <span className="assignee-avatar">
                            {getAssigneeName(ticket)?.charAt(0).toUpperCase()}
                          </span>
                          {getAssigneeName(ticket)}
                        </span>
                      )}
                      {ticket.tags.length > 0 && (
                        <span className="tags">
                          {ticket.tags.slice(0, 2).map((tagName, i) => {
                            const tagObj = getTagObj(tagName)
                            return (
                              <span
                                key={i}
                                className="tag"
                                style={tagObj ? { background: `${tagObj.color}20`, color: tagObj.color } : {}}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setFilter('tag', tagName)
                                }}
                              >
                                {tagName}
                              </span>
                            )
                          })}
                        </span>
                      )}
                      {ticket.department && (
                        <span className="department-badge" onClick={(e) => {
                          e.stopPropagation()
                          setFilter('department', ticket.department!)
                        }}>
                          <i className="bi bi-building"></i>
                          {getDepartmentLabel(ticket.department)}
                        </span>
                      )}
                      {ticket.is_internal && (
                        <span className="internal-ticket-badge">פנימי</span>
                      )}
                      {ticket.follow_up_date && (
                        <span
                          className={`follow-up-badge ${new Date(ticket.follow_up_date) <= new Date() ? 'overdue' : 'upcoming'}`}
                          title={ticket.follow_up_note || ''}
                        >
                          <i className={`bi ${new Date(ticket.follow_up_date) <= new Date() ? 'bi-bell-fill' : 'bi-bell'}`}></i>
                          {moment(ticket.follow_up_date).format('DD/MM HH:mm')}
                          {ticket.follow_up_note && <span className="followup-note-preview"> · {ticket.follow_up_note}</span>}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="ticket-dates">
                    <span className="ticket-date created" title="תאריך יצירה">
                      <i className="bi bi-calendar-plus"></i>
                      נוצר: {moment(ticket.created_at).format('DD/MM/YY HH:mm')}
                    </span>
                    <span className="ticket-date updated" title="נצפה לאחרונה">
                      <i className="bi bi-eye"></i>
                      נצפה: {getTimeAgo(ticket.updated_at)}
                    </span>
                  </div>
                </div>
              ))}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-bar">
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(p => p - 1)}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let page: number
                    if (totalPages <= 5) {
                      page = i + 1
                    } else if (currentPage <= 3) {
                      page = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      page = totalPages - 4 + i
                    } else {
                      page = currentPage - 2 + i
                    }
                    return (
                      <button
                        key={page}
                        className={`page-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    )
                  })}
                  <button
                    className="page-btn"
                    onClick={() => setCurrentPage(p => p + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Tag Manager Modal */}
      <TagManager
        show={showTagManager}
        onClose={() => setShowTagManager(false)}
        onTagsChanged={loadData}
      />

      {/* Create Ticket Modal */}
      <CreateTicketModal
        show={showCreateTicket}
        onClose={() => setShowCreateTicket(false)}
        onCreated={(ticketId) => {
          setShowCreateTicket(false)
          navigate(`/admin/tickets/${ticketId}`)
        }}
        admins={admins}
      />
    </div>
  )
}

export default TicketsPage
